"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfileData } from "@/types/modules";
import { renderInlineMarkdown, stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { Globe } from "lucide-react";
import { SiInstagram as Instagram, SiX as Twitter } from "@icons-pack/react-simple-icons";
import { cdn } from "@/lib/cdn";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { toSafeHttpsUrl, toSafeSocialUrl } from "@/lib/social-urls";

function Linkedin({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}


interface ModuleIntroProps {
    title: string;
    summary: string;
    author: ProfileData;
    noSkeleton?: boolean;
    grayscale?: boolean;
}

export default function ModuleIntro({ title, summary, author, noSkeleton = false, grayscale }: ModuleIntroProps) {
    const { t } = useViewerLanguage();
    const firstName = author?.firstName || author?.first_name || "";
    const lastName = author?.lastName || author?.last_name || "";
    const websiteUrl = toSafeHttpsUrl(author?.website);
    const twitterUrl = toSafeSocialUrl(author?.twitter, "twitter");
    const linkedinUrl = toSafeSocialUrl(author?.linkedin, "linkedin");
    const instagramUrl = toSafeSocialUrl(author?.instagram, "instagram");
    const loading = !noSkeleton && !firstName && !lastName;
    const initials = (firstName[0] ?? "")?.toUpperCase() + (lastName[0] ?? "")?.toUpperCase();
    const name = `${firstName} ${lastName}`.trim();

    return (
        <div
            className="flex flex-col justify-center gap-4 h-full text-start "
        >
            <div
                className={cn(
                    "flex flex-col gap-3",
                    grayscale ? "bg-neutral-900 dark:bg-neutral-800" : "bg-primary",
                    "p-4",
                )}
            >
                <p className="text-sm text-white">
                    {t("Powered by")} <a href="https://handyout.com" className="underline">©handyout</a>
                </p>
                <h1 className={`text-2xl font-medium text-white ${MODULE_LABEL_WRAP.heading}`}>
                    {stripInlineMarkdown(title)}
                </h1>
                <p className={`text-sm text-white ${MODULE_LABEL_WRAP.body}`}>
                    {renderInlineMarkdown(summary)}
                </p>
                <span className="text-xs font-medium text-white">{t("by {name}", { name })}</span>
            </div>

           {loading ? (
              <div className="grid grid-cols-7 bg-muted-foreground/10 rounded-md overflow-hidden">
                <Skeleton className="col-span-3 w-full h-24 rounded-none" />
                <div className="inline-flex flex-col gap-2 col-span-4 pl-4 py-2">
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-4 w-20" />
                    <Separator />
                    <Skeleton className="h-10 w-full" />
                </div>
              </div>
           ) : (
              <div className="grid grid-cols-7 bg-muted-foreground/10 rounded-md overflow-hidden">
                <Avatar className="col-span-3 w-full h-full rounded-none">
                    <AvatarImage
                        src={author?.avatar || author?.avatar_url || cdn("profiles/profile_placeholder.png")}
                        alt={name}
                        className="object-cover"
                    />
                    <AvatarFallback className="text-md">
                        {initials}
                    </AvatarFallback>
                </Avatar>

                <div className="inline-flex flex-col gap-2 col-span-4 pl-4 py-2">
                    <h3 className={`text-lg font-medium ${MODULE_LABEL_WRAP.row}`}>{name}</h3>
                    {author?.role && (
                        <p className={`text-sm ${MODULE_LABEL_WRAP.row}`}>{stripInlineMarkdown(author.role)}</p>
                    )}
                    {author?.bio && (
                        <>
                            <Separator />
                            <p className="text-sm text-muted-foreground">{renderInlineMarkdown(author.bio)}</p>
                        </>
                    )}
                    {(websiteUrl || twitterUrl || linkedinUrl || instagramUrl) && (
                        <>
                            <Separator />
                            <div className="flex gap-3">
                                {websiteUrl && (
                                    <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                        <Globe className="size-5" />
                                    </a>
                                )}
                                {twitterUrl && (
                                    <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                        <Twitter className="size-5" />
                                    </a>
                                )}
                                {linkedinUrl && (
                                    <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                        <Linkedin className="size-5" />
                                    </a>
                                )}
                                {instagramUrl && (
                                    <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                        <Instagram className="size-5" />
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </div>
              </div>
           )}
        </div>
    );
}
